import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Text,
    useDisclosure,
    useMultiStyleConfig
} from '@chakra-ui/react'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import Sticky from 'react-sticky-el'

// Project Components
import ShopLookDrawerProducts from './ShopLookDrawerProducts'
import {ChevronRightIcon} from '../../components/custom-icons/index.jsx'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

/**
 * ShopLookDrawer is a React component that renders a drawer interface for shopping a curated look.
 *
 * This component provides a user interface for browsing and selecting product variants, and adding
 * them to a shopping cart. It leverages various hooks and context providers to manage state, handle
 * user interactions, and perform asynchronous operations related to shopping cart management.
 *
 * Props:
 * - products (Array): An array of product objects to be displayed within the drawer. Each product
 *   object should contain information about its variants, images, and pricing.
 * - buttonProps (Object): An object containing properties for customizing the appearance and behavior
 *   of the button that opens the drawer. This includes styles and text for the button.
 * - mainTitle (string): A string representing the main title to be displayed at the top of the drawer.
 *
 * The component uses the following hooks and context providers:
 * - useDisclosure: Manages the open/close state of the drawer.
 * - useMultiStyleConfig: Retrieves style configurations for the component.
 * - useShopperBasketsMutationHelper: Provides functions for adding items to the shopping cart.
 * - useCurrency: Retrieves the active currency for displaying product prices.
 * - useLocation: Provides access to the current location object, used for detecting route changes.
 * - useIntl: Provides internationalization support for formatting messages.
 * - useBreakpointValue: Determines whether the component is being viewed on a mobile device.
 * - useToast: Displays toast messages for user feedback.
 * - useAddToCartModalContext: Manages the state of the add-to-cart modal.
 *
 * The component also defines two internal functions:
 * - handleChangeVariant: Updates the current product variant and hides the options message.
 * - handleCartItem: Attempts to add a selected product variant to the shopping cart and manages
 *   the display of success or error messages.
 *
 * The component renders a button that opens the drawer, and within the drawer, it displays a list
 * of products with their images, names, prices, and available variants. Users can select a variant
 * and add it to their cart. If no variant is selected, a message prompts the user to select options.
 */
const ShopLookDrawer = ({products, buttonProps, mainTitle}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useMultiStyleConfig('PerfectYourLook')
    const {triggerShopTheLookEvent} = useGTM()
    const {currency: activeCurrency} = useCurrency()

    const drawerMenuTriggerRef = useRef(null)

    const {
        styles: buttonStyles,
        text: buttonText,
        isFullOpacity: isFullButtonOpacity = false,
        ...rest
    } = buttonProps ?? {}

    /**
     * Handles the button click event by performing the following actions:
     * 1. Invokes the `onOpen` function to execute any logic associated with opening
     *    the Shop Look Drawer.
     * 2. Triggers the "Shop The Look" event for Google Tag Manager (GTM) integration
     *    by calling the `triggerShopTheLookEvent` function. This is used for tracking
     *    and analytics purposes to monitor user interactions with the Shop Look feature.
     */
    const handleButtonClick = () => {
        onOpen()

        // GTM Integration
        triggerShopTheLookEvent()
    }

    return (
        <>
            <Button
                size="sm"
                onClick={handleButtonClick}
                {...styles[buttonStyles]}
                ref={drawerMenuTriggerRef}
                {...(isFullButtonOpacity && {opacity: 1})}
                {...rest}
            >
                <Text textTransform="uppercase" role="heading" aria-level="2">
                    {buttonText}
                    <ChevronRightIcon
                        boxSize="12px"
                        color="black"
                        display="inline-block"
                        ml="4px"
                        transform="translateY(-1px)"
                    />
                </Text>
            </Button>
            <Drawer
                isOpen={isOpen}
                placement="right"
                size={'lg'}
                onClose={onClose}
                finalFocusRef={drawerMenuTriggerRef}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>
                        <Text as={'h1'}>
                            {mainTitle || buttonText}
                        </Text>
                    </DrawerHeader>
                    <DrawerBody>
                        <ShopLookDrawerProducts
                            products={products}
                            onClose={onClose}
                            styles={styles}
                            activeCurrency={activeCurrency}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}

ShopLookDrawer.propTypes = {
    products: PropTypes.array,
    buttonProps: PropTypes.object,
    mainTitle: PropTypes.string
}

export default ShopLookDrawer
